import type { UserDto } from "@stylingcv/dto";
import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useEffect } from "react";

import { axios } from "@/client/libs/axios";
import { useAuthStore } from "@/client/stores/auth";

export const fetchUser = async (): Promise<UserDto | null> => {
  try {
    const response = await axios.get<UserDto | null, AxiosResponse<UserDto | null>>("/user/me");

    return response.data;
  } catch (error: any) {
    // If 401 Unauthorized or 403 Forbidden, treat as guest (not logged in)
    // Check both response.status and message for robustness
    // Axios interceptors may transform the error, so we check multiple possible locations
    const status = error?.response?.status || error?.status;
    const message = error?.message?.toLowerCase() || "";
    const isAuthError = status === 401 || 
                       status === 403 || 
                       message.includes("unauthorized") || 
                       message.includes("forbidden") ||
                       message.includes("401") ||
                       message.includes("session expired");
    
    if (isAuthError) {
      // Silently return null for guests - this is expected behavior
      // Don't log 401s as they're normal for guest users
      return null;
    }
    // Log and throw other unexpected errors (but not in development for cleaner console)
    if (process.env.NODE_ENV === "production") {
      console.error("Error fetching user data:", error);
    }
    throw error;
  }
};

export const useUser = () => {
  const setUser = useAuthStore((state) => state.setUser);
  const subscriptionTier = useAuthStore((state) => state.subscriptionTier);
  const syncSubscriptionTier = useAuthStore((state) => state.syncSubscriptionTier);

  const {
    error,
    isPending: loading,
    data: user,
  } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUser,
    staleTime: 10 * 60 * 1000, // Increase to 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    refetchOnWindowFocus: false, // Disable window focus refetch
    refetchOnMount: false, // Only refetch on explicit refresh
    refetchInterval: false, // Disable automatic polling
    retry: 1, // Reduce retry attempts
  });

  useEffect(() => {
    if (user) {
      // Update the user in the store and sync subscription tier
      setUser(user);
      // Force sync after setting user to ensure consistency
      setTimeout(() => {
        syncSubscriptionTier();
      }, 100);
    } else if (user === null) {
      // Guest user, clear user in store
      setUser(null);
    }
  }, [user, setUser, syncSubscriptionTier]);

  // Only treat as error if error exists and is not 401
  const isAuthError = error && (error as any)?.response?.status !== 401;

  return { user, loading, error: isAuthError ? error : undefined };
};
